import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cmsService } from '../../services/cmsService';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Save, Home, FileText, Award, ListOrdered, HelpCircle, 
  Phone, ChevronRight, Loader2, Check, Plus, Trash2, GripVertical, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { companyInfo } from '../../data/mock';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const [activeSection, setActiveSection] = useState('hero');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data states
  const [hero, setHero] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [process, setProcess] = useState([]);
  const [faq, setFaq] = useState([]);
  const [contact, setContact] = useState(null);

  const sections = [
    { id: 'hero', label: 'Hero / Inicio', icon: Home },
    { id: 'benefits', label: 'Beneficios', icon: Award },
    { id: 'process', label: 'Proceso', icon: ListOrdered },
    { id: 'faq', label: 'Preguntas Frecuentes', icon: HelpCircle },
    { id: 'contact', label: 'Contacto', icon: Phone },
    { id: 'blog', label: 'Blog', icon: FileText, link: '/admin/blog' },
  ];

  const iconOptions = [
    'Banknote', 'ShieldCheck', 'Zap', 'Settings2', 'TrendingUp', 'Users',
    'Clock', 'CheckCircle', 'Star', 'Heart', 'Target', 'Award'
  ];

  useEffect(() => {
    loadSectionData(activeSection);
  }, [activeSection]);

  const loadSectionData = async (section) => {
    setLoading(true);
    try {
      switch (section) {
        case 'hero':
          const heroData = await cmsService.getHero();
          setHero(heroData);
          break;
        case 'benefits':
          const benefitsData = await cmsService.getBenefits();
          setBenefits(benefitsData);
          break;
        case 'process':
          const processData = await cmsService.getProcess();
          setProcess(processData);
          break;
        case 'faq':
          const faqData = await cmsService.getFAQ();
          setFaq(faqData);
          break;
        case 'contact':
          const contactData = await cmsService.getContact();
          setContact(contactData);
          break;
        default:
          break;
      }
    } catch (err) {
      toast.error('Error al cargar los datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      switch (activeSection) {
        case 'hero':
          await cmsService.updateHero(hero);
          break;
        case 'benefits':
          await cmsService.updateBenefits(benefits);
          break;
        case 'process':
          await cmsService.updateProcess(process);
          break;
        case 'faq':
          await cmsService.updateFAQ(faq);
          break;
        case 'contact':
          await cmsService.updateContact(contact);
          break;
        default:
          break;
      }
      toast.success('¡Cambios guardados correctamente!');
    } catch (err) {
      toast.error('Error al guardar los cambios');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Render functions for each section
  const renderHeroEditor = () => {
    if (!hero) return null;
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700">
            Esta sección es lo primero que ven tus visitantes. Usa textos claros y directos.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Título Principal</label>
          <Input
            value={hero.title}
            onChange={(e) => setHero({ ...hero, title: e.target.value })}
            placeholder="Convierte tus Facturas en Capital Inmediato"
            className="text-lg"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Subtítulo</label>
          <Textarea
            value={hero.subtitle}
            onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
            placeholder="Soluciones de factoring ágiles..."
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Texto del Botón (CTA)</label>
          <Input
            value={hero.cta_text}
            onChange={(e) => setHero({ ...hero, cta_text: e.target.value })}
            placeholder="Solicitar Asesoría Gratis"
          />
        </div>

        <div className="border-t pt-6 mt-6">
          <h4 className="font-semibold text-gray-900 mb-4">Estadísticas</h4>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
              <label className="text-sm font-medium text-gray-700">Estadística 1</label>
              <Input
                value={hero.stat1_value}
                onChange={(e) => setHero({ ...hero, stat1_value: e.target.value })}
                placeholder="+200"
              />
              <Input
                value={hero.stat1_label}
                onChange={(e) => setHero({ ...hero, stat1_label: e.target.value })}
                placeholder="Empresas asesoradas"
              />
            </div>
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
              <label className="text-sm font-medium text-gray-700">Estadística 2</label>
              <Input
                value={hero.stat2_value}
                onChange={(e) => setHero({ ...hero, stat2_value: e.target.value })}
                placeholder="+$50M"
              />
              <Input
                value={hero.stat2_label}
                onChange={(e) => setHero({ ...hero, stat2_label: e.target.value })}
                placeholder="Desembolsados"
              />
            </div>
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
              <label className="text-sm font-medium text-gray-700">Estadística 3</label>
              <Input
                value={hero.stat3_value}
                onChange={(e) => setHero({ ...hero, stat3_value: e.target.value })}
                placeholder="24h"
              />
              <Input
                value={hero.stat3_label}
                onChange={(e) => setHero({ ...hero, stat3_label: e.target.value })}
                placeholder="Compromiso a desembolsar"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBenefitsEditor = () => {
    const addBenefit = () => {
      setBenefits([...benefits, {
        id: Date.now().toString(),
        order: benefits.length + 1,
        title: '',
        description: '',
        icon: 'Banknote'
      }]);
    };

    const removeBenefit = (index) => {
      setBenefits(benefits.filter((_, i) => i !== index));
    };

    const updateBenefit = (index, field, value) => {
      const updated = [...benefits];
      updated[index] = { ...updated[index], [field]: value };
      setBenefits(updated);
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700">
            Los beneficios muestran por qué elegir FACTORYZA. Mantén los textos cortos y claros.
          </p>
        </div>

        {benefits.map((benefit, index) => (
          <div key={benefit.id} className="bg-gray-50 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GripVertical className="w-5 h-5 text-gray-400" />
                <span className="font-semibold text-gray-700">Beneficio {index + 1}</span>
              </div>
              <button
                onClick={() => removeBenefit(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Título</label>
                <Input
                  value={benefit.title}
                  onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                  placeholder="Liquidez Inmediata"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Icono</label>
                <select
                  value={benefit.icon}
                  onChange={(e) => updateBenefit(index, 'icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Descripción</label>
              <Textarea
                value={benefit.description}
                onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                placeholder="Recibe hasta el 90% del valor de tus facturas..."
                rows={2}
              />
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          onClick={addBenefit}
          className="w-full border-dashed border-2"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Beneficio
        </Button>
      </div>
    );
  };

  const renderProcessEditor = () => {
    const addStep = () => {
      setProcess([...process, {
        id: Date.now().toString(),
        order: process.length + 1,
        title: '',
        description: ''
      }]);
    };

    const removeStep = (index) => {
      setProcess(process.filter((_, i) => i !== index));
    };

    const updateStep = (index, field, value) => {
      const updated = [...process];
      updated[index] = { ...updated[index], [field]: value };
      setProcess(updated);
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700">
            Explica el proceso de manera simple. Los clientes quieren saber qué tan fácil es trabajar contigo.
          </p>
        </div>

        {process.map((step, index) => (
          <div key={step.id} className="bg-gray-50 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#0092ca] text-white rounded-lg flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                <span className="font-semibold text-gray-700">Paso {index + 1}</span>
              </div>
              <button
                onClick={() => removeStep(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Título</label>
              <Input
                value={step.title}
                onChange={(e) => updateStep(index, 'title', e.target.value)}
                placeholder="Contacto Inicial"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Descripción</label>
              <Textarea
                value={step.description}
                onChange={(e) => updateStep(index, 'description', e.target.value)}
                placeholder="Comunícate con nosotros vía WhatsApp o correo..."
                rows={2}
              />
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          onClick={addStep}
          className="w-full border-dashed border-2"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Paso
        </Button>
      </div>
    );
  };

  const renderFAQEditor = () => {
    const addFAQ = () => {
      setFaq([...faq, {
        id: Date.now().toString(),
        order: faq.length + 1,
        question: '',
        answer: ''
      }]);
    };

    const removeFAQ = (index) => {
      setFaq(faq.filter((_, i) => i !== index));
    };

    const updateFAQ = (index, field, value) => {
      const updated = [...faq];
      updated[index] = { ...updated[index], [field]: value };
      setFaq(updated);
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700">
            Las preguntas frecuentes ayudan a resolver dudas y mejorar tu SEO. Piensa en las preguntas que más te hacen tus clientes.
          </p>
        </div>

        {faq.map((item, index) => (
          <div key={item.id} className="bg-gray-50 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-700">Pregunta {index + 1}</span>
              <button
                onClick={() => removeFAQ(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Pregunta</label>
              <Input
                value={item.question}
                onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                placeholder="¿Qué es el factoring?"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Respuesta</label>
              <Textarea
                value={item.answer}
                onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                placeholder="El factoring es un servicio financiero que..."
                rows={3}
              />
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          onClick={addFAQ}
          className="w-full border-dashed border-2"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Pregunta
        </Button>
      </div>
    );
  };

  const renderContactEditor = () => {
    if (!contact) return null;
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700">
            Esta información aparece en la sección de contacto y en el footer de tu sitio.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Teléfono / WhatsApp</label>
            <Input
              value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })}
              placeholder="+507 6586-5503"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Número WhatsApp (sin +)</label>
            <Input
              value={contact.whatsapp}
              onChange={(e) => setContact({ ...contact, whatsapp: e.target.value })}
              placeholder="50765865503"
            />
            <p className="text-xs text-gray-500">Solo números, sin espacios ni símbolos</p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Correo Electrónico</label>
          <Input
            type="email"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            placeholder="info@factoryza.com"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Dirección</label>
          <Textarea
            value={contact.address}
            onChange={(e) => setContact({ ...contact, address: e.target.value })}
            placeholder="Calle Isaac Hanono Missri, Torre Metrobank..."
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">LinkedIn URL</label>
          <Input
            value={contact.linkedin}
            onChange={(e) => setContact({ ...contact, linkedin: e.target.value })}
            placeholder="https://www.linkedin.com/company/factoryza/"
          />
        </div>
      </div>
    );
  };

  const renderSectionContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#0092ca] animate-spin" />
        </div>
      );
    }

    switch (activeSection) {
      case 'hero':
        return renderHeroEditor();
      case 'benefits':
        return renderBenefitsEditor();
      case 'process':
        return renderProcessEditor();
      case 'faq':
        return renderFAQEditor();
      case 'contact':
        return renderContactEditor();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img
                  src={companyInfo.logoBlack}
                  alt="FACTORYZA"
                  className="h-10 w-auto"
                />
              </Link>
              <span className="text-gray-300">|</span>
              <h1 className="font-semibold text-gray-900">Panel de Administración</h1>
            </div>
            <nav className="flex items-center gap-4">
              <Link to="/" className="text-gray-600 hover:text-[#0092ca] font-medium">
                Ver Sitio
              </Link>
              <button
                onClick={logout}
                className="flex items-center gap-2 text-gray-600 hover:text-red-500 font-medium transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Salir
              </button>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4 px-3">Secciones</h3>
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  if (section.link) {
                    return (
                      <Link
                        key={section.id}
                        to={section.link}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-[#0092ca] transition-colors"
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{section.label}</span>
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      </Link>
                    );
                  }
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                        activeSection === section.id
                          ? 'bg-[#0092ca]/10 text-[#0092ca]'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm">
              {/* Section Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {sections.find(s => s.id === activeSection)?.label}
                </h2>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#0092ca] hover:bg-[#007bb5] text-white rounded-full"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Guardando...
                    </span>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>

              {/* Section Content */}
              <div className="p-6">
                {renderSectionContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
